/** @jsx jsx */
/** @jsxFrag React.Fragment */

import * as React from 'react';
import { connect } from 'react-redux';
import { css, jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { Link } from '@helpcenter/analytics-components';
import { Button } from '@helpcenter/analytics-components/src/button';
import type { ButtonProps } from '@helpcenter/analytics-components/src/button';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import {
    isHelpCenterNewDropdownEnabled,
    isUseHelpCenterRefresh,
    isVirtualAgentHeaderButtonEnabled,
} from '@helpcenter/feature-flags';
import { getHelpCenterAri } from '@helpcenter/meta';
import * as grid from '@helpcenter/styles/src/grid';
import * as mixins from '@helpcenter/styles/src/mixins';
import { FormattedMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { usePathParam } from 'react-resource-router';
import type { State } from 'state';
import type { UserRequestsModel } from 'state/persisted/user/reducer';
import type { UserRequestsCounts } from 'state/persisted/user-requests-counts';
import { getHelpCenterTitleColor, getThemeColor } from 'state/selectors/help-center';
import { LogoMemoizedHOC } from 'view/common/logo';
import { HEADER_HEIGHT } from 'view/common/styled';
import { Search as SearchV1 } from 'view/search';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { Skeleton } from '@atlaskit/avatar';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';

import { CsmRequestsHeaderLink } from '@atlassian/csm-requests-common/src/ui/csm-requests-header-link';
import { AvatarMenuV2 } from '@atlassian/help-center-avatar-menu';

import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { componentWithCondition } from '@atlassian/help-center-common-util/feature-flags-utils';
import { getHelpCenterUrl } from '@atlassian/help-center-common-util/url';
import { Search } from '@atlassian/help-center-search';
import { CustomiseMenu } from '../customise-menu';
import { VirtualServiceAgentHeader } from '../virtual-service-agent-header';
import i18n from './messages';

const isCompiledCssEnabled = (): boolean => FeatureGates.checkGate('help_center_login_compiled_css');

interface Organisations {
    name: string;
    id: number;
}

export interface Props {
    isAnonymous?: boolean;
    isUserLoaded?: boolean;
    userLoggedIn?: boolean;
    loginUrl?: string;
    logoTextColor: 'banner' | 'theme';
    hideSearchButton?: boolean;
    isCustomizing?: boolean;
    userRequest: UserRequestsModel | UserRequestsCounts; //TODO: remove UserRequestsModel when removing isSplitRequestCountsFromUserModelEnabled
    organisations: Organisations[];
    anonymousEnabled?: boolean;
    showThemedBackground?: boolean;
    disableAllHeaderLinks?: boolean;
    headerBackgroundColor?: string;
    logoUrl?: string;
    name?: string;
}

const Header: React.FunctionComponent<Props> = ({
    isAnonymous,
    isUserLoaded,
    userLoggedIn,
    loginUrl,
    logoTextColor,
    hideSearchButton,
    anonymousEnabled,
    showThemedBackground,
    disableAllHeaderLinks,
    headerBackgroundColor,
    logoUrl,
    name,
}) => {
    di(isHelpCenterNewDropdownEnabled);
    di(isCSMHelpCenter);
    di(getHelpCenterAri);
    di(isVirtualAgentHeaderButtonEnabled);
    const isCSM = isCSMHelpCenter(getEnv().helpCenterType);
    const showPortalLink = isCSM ? !!logoUrl : true;
    const [portalId] = usePathParam('portalId');

    const { formatMessage } = useIntl();

    const defaultLoginUrl = `/user/login?destination=${getHelpCenterUrl().replace(/\//g, '')}`;
    const authenticatedNavLinks = React.useMemo(
        () => (
            <>
                {isVirtualAgentHeaderButtonEnabled() && !isCSM && (
                    <NavItem role="listitem">
                        <ErrorBoundary packageName={'viewLayoutHeaderDumb'} id={'virtualAgentHeaderButton'}>
                            <React.Suspense fallback={null}>
                                <VirtualServiceAgentHeader portalId={portalId ?? ''} />
                            </React.Suspense>
                        </ErrorBoundary>
                    </NavItem>
                )}
                {isHelpCenterNewDropdownEnabled() && (
                    <NavItem>
                        <ErrorBoundary packageName={'viewLayoutHeaderDumb'} id={'customizeMenu'}>
                            <CustomiseMenu disableCustomiseMenu={disableAllHeaderLinks} />
                        </ErrorBoundary>
                    </NavItem>
                )}

                {isCSM && (
                    <NavItem role="listitem">
                        <ErrorBoundary packageName="viewLayoutHeaderDumb" id="CsmRequestsHeaderLink">
                            <Box xcss={requestsLinkStyles}>
                                <CsmRequestsHeaderLink />
                            </Box>
                        </ErrorBoundary>
                    </NavItem>
                )}

                <NavItem>
                    <AnalyticsContext data={{ actionSubjectId: 'navbarAvatar' }}>
                        <AvatarMenuV2 disableAvatarMenu={disableAllHeaderLinks} />
                    </AnalyticsContext>
                </NavItem>
            </>
        ),
        [disableAllHeaderLinks, isCSM, portalId]
    );

    const unauthenticatedNavLinks = (
        <NavItem role="listitem">
            <ExternalNavLink>
                <LoginButton href={loginUrl ? loginUrl : defaultLoginUrl} actionSubjectId="navbarLogin">
                    <FormattedMessage {...i18n.login} />
                </LoginButton>
            </ExternalNavLink>
        </NavItem>
    );

    const anonymousAccessNotEnbledAndUserNotLoggedIn = !(!!anonymousEnabled || !!userLoggedIn);

    let portalLinkUrl;
    let portalLinkAriaLabel;
    if (isAnonymous || anonymousAccessNotEnbledAndUserNotLoggedIn) {
        portalLinkUrl = defaultLoginUrl;
        portalLinkAriaLabel = formatMessage(i18n.helpCenter);
    } else {
        portalLinkUrl = getHelpCenterUrl();
        portalLinkAriaLabel =
            FeatureGates.checkGate('fix_logo_aria_label') && name ? name : formatMessage(i18n.helpCenter);
    }

    return (
        <AnalyticsContext data={{ source: 'navbar' }}>
            <Root showThemedBackground={Boolean(showThemedBackground)} headerBackgroundColor={headerBackgroundColor}>
                {showPortalLink && (
                    <PortalLink
                        to={portalLinkUrl}
                        logoTextColor={logoTextColor}
                        actionSubjectId="navbarHelpCenter"
                        disabled={Boolean(disableAllHeaderLinks)}
                        tabIndex={disableAllHeaderLinks ? -1 : undefined}
                        aria-label={portalLinkAriaLabel}
                    >
                        <React.Suspense
                            fallback={
                                <UFOLoadHold name="header-dumb-logo-fallback">
                                    <Skeleton appearance="square" size="medium" />
                                </UFOLoadHold>
                            }
                        >
                            <LogoMemoizedHOC />
                        </React.Suspense>
                    </PortalLink>
                )}

                <Nav>
                    <NavList role="list">
                        {isUserLoaded && (
                            <>
                                {hideSearchButton || (
                                    <NavItem role="listitem">
                                        {/* Upgrading @atlaskit/platform; fix these violations. */}
                                        {/* eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-registration */}
                                        {fg('helpcenter_search_api_changes') ? (
                                            <Search trigger="icon" disableSearch={disableAllHeaderLinks} />
                                        ) : (
                                            <SearchV1 trigger="icon" disableSearch={disableAllHeaderLinks} />
                                        )}
                                    </NavItem>
                                )}

                                {userLoggedIn ? authenticatedNavLinks : unauthenticatedNavLinks}
                            </>
                        )}
                    </NavList>
                </Nav>
            </Root>
        </AnalyticsContext>
    );
};

export default Header;
const rootStyles = css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: token('space.100', '8px'),
    paddingLeft: token('space.100', '8px'),
    overflow: 'hidden',
});

const refreshRootStyles = css({
    paddingLeft: token('space.200', '16px'),
});

interface RootProps {
    headerBackgroundColor?: string;
    showThemedBackground: boolean;
    themeColor: string;
}
const RootCompiled = (props: React.PropsWithChildren<RootProps>) => {
    let backgroundColor: string;
    if (props.headerBackgroundColor) {
        backgroundColor = props.headerBackgroundColor;
    } else {
        backgroundColor = props.showThemedBackground ? props.themeColor : '';
    }
    return (
        <header
            css={[rootStyles, isUseHelpCenterRefresh() && refreshRootStyles]}
            style={{
                backgroundColor,
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
                height: `${HEADER_HEIGHT}px`,
            }}
        >
            {props.children}
        </header>
    );
};
const RootCompiledConnected = connect((state: State) => {
    return {
        themeColor: getThemeColor(state),
    };
})(RootCompiled);

// Nav line height set to 0 so it is correctly vertically aligned.
const NAV_LINE_HEIGHT = 0;
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, rulesdir/styled-component-order -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const RootStyled = styled.header<{ showThemedBackground: boolean; headerBackgroundColor?: string }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    ${() => {
        if (isUseHelpCenterRefresh()) {
            return `
                padding-left: ${token('space.200', '16px')};
            `;
        }
        return `
            padding-left: ${token('space.100', '8px')};
            `;
    }}
    padding-right: ${token('space.100', '8px')};
    height: ${HEADER_HEIGHT}px;
    overflow: hidden;
    ${(props) => {
        if (props.headerBackgroundColor) {
            return props.headerBackgroundColor ? `background-color: ${props.headerBackgroundColor}` : '';
        }
        return props.showThemedBackground ? `background-color: ${props.theme.color}` : '';
    }}
`;

const Root = componentWithCondition(isCompiledCssEnabled, RootCompiledConnected, RootStyled);

const navStyles = css({
    /* Ensures header icons are vertically aligned consistently across browsers */
    // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
    lineHeight: NAV_LINE_HEIGHT,
    marginLeft: 'auto',
});
const NavCompiled = (props: { children: React.ReactNode }) => <nav css={navStyles}>{props.children}</nav>;
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, rulesdir/styled-component-order -- Disabled to rollout go/ui-styling-standard tooling, please resolve,rulesdir/styled-component-order
const NavStyled = styled.nav`
    /* Ensures header icons are vertically aligned consistently accross browsers */
    line-height: ${NAV_LINE_HEIGHT};
    margin-left: auto;
`;

const Nav = componentWithCondition(isCompiledCssEnabled, NavCompiled, NavStyled);

const navListStyles = css({
    margin: 0,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
});
const NavListCompiled = (props: { children: React.ReactNode; role?: string }) => (
    <div css={navListStyles} role={props.role}>
        {props.children}
    </div>
);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, rulesdir/styled-component-order -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const NavListStyled = styled.div`
    margin: 0;
    padding: 0;
    display: flex;
    align-items: center;
`;

const NavList = componentWithCondition(isCompiledCssEnabled, NavListCompiled, NavListStyled);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, rulesdir/styled-component-order -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const NavItem = styled.div`
    display: inline;
    margin: 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, rulesdir/styled-component-order -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LoginButton = styled((props: Omit<ButtonProps, 'theme'>) => <Button {...props} />)`
    && {
        padding-left: ${token('space.150', '12px')};
        padding-right: ${token('space.150', '12px')};
    }
`;

// We need to override this because ADG button is transparent and not actually grey.
// This makes sure you can see the button against the banner.
// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, rulesdir/styled-component-order -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const ExternalNavLink = styled.div`
    ${mixins.subtleThemeColorBackground};
    border: ${token('border.width.selected', '2px')} ${token('color.border.inverse', '#FFFFFF')} solid;
    border-radius: ${grid.multiple(2).px};
    margin-left: ${token('space.100', '8px')};
`;

interface PortalLinkProps {
    logoTextColor: 'banner' | 'theme';
    disabled?: boolean;
    themeColor?: string;
    titleColor?: string;
}

const PortalLinkCompiled = (props: React.ComponentProps<typeof Link> & PortalLinkProps) => {
    const { logoTextColor, disabled, themeColor, titleColor, children, ...linkProps } = props;
    return (
        <Link
            to={linkProps.to}
            actionSubjectId={linkProps.actionSubjectId}
            tabIndex={linkProps.tabIndex}
            aria-label={linkProps['aria-label']}
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
            className={'cv-exclude-from-customisation'}
            style={{
                pointerEvents: disabled ? 'none' : 'auto',
                color: logoTextColor === 'banner' ? titleColor : token('color.link', themeColor),
            }}
            // @ts-ignore
            disabled={disabled}
        >
            {children}
        </Link>
    );
};

const PortalLinkCompiledConnected = connect((state: State) => ({
    themeColor: getThemeColor(state),
    titleColor: getHelpCenterTitleColor(state),
}))(PortalLinkCompiled);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const PortalLinkStyled = styled<React.ComponentProps<typeof Link> & PortalLinkProps>(({ logoTextColor, ...props }) => (
    // eslint-disable-next-line @atlassian/a11y/anchor-has-content -- Disabling existing violation, should be fixed when revisited.
    <Link {...props} />
)).attrs({
    className: 'cv-exclude-from-customisation',
})`
    /* stylelint-disable scale-unlimited/declaration-strict-value */
    color: ${(props) =>
        props.logoTextColor === 'banner' ? props.theme.titleColor : token('color.link', props.theme.color)};

    &:hover {
        color: ${(props) =>
            props.logoTextColor === 'banner' ? props.theme.titleColor : token('color.link', props.theme.color)};
    }
    pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

const PortalLink = componentWithCondition(isCompiledCssEnabled, PortalLinkCompiledConnected, PortalLinkStyled);

const requestsLinkStyles = xcss({
    marginInlineEnd: 'space.075',
});
